import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useStationPrice from "../../hooks/useStationPrice";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Divider } from "primereact/divider";
import "./DefaultPrices.css";

const formatDate = (isoString: string) => {
  if (!isoString) return "N/A";
  return new Date(isoString).toLocaleString();
};

const DefaultPrices = () => {
  const navigate = useNavigate();
  const ac = useStationPrice();
  const dc = useStationPrice();

  // Depends on the fetchers rather than the hook objects: useStationPrice
  // returns a fresh object each render, so depending on ac/dc themselves would
  // refetch forever. The fetchers are useCallback-stable.
  const fetchAc = ac.getStationPriceByType;
  const fetchDc = dc.getStationPriceByType;

  useEffect(() => {
    fetchAc("AC");
    fetchDc("DC");
  }, [fetchAc, fetchDc]);

  return (
    <div className="price-container">
      <div className="price-page-wrapper">
        <Message
          severity="info"
          className="mb-4 w-full"
          content={
            <span>
              <i className="pi pi-info-circle mr-2" />
              <strong>Default Prices</strong> apply to all charging stations
              that are <strong>not assigned a community price</strong>. Changes
              here affect the fallback tariff used across the network.
            </span>
          }
        />

        <div className="grid">
          <div className="col-12 md:col-6">
            <PricePanel
              label="AC"
              hook={ac}
              onEdit={() =>
                navigate("/default-prices/edit", {
                  state: {
                    stationPrices: ac.stationPrices,
                    originalResponse: ac.originalResponse,
                    stationType: "AC",
                  },
                })
              }
            />
          </div>
          <div className="col-12 md:col-6">
            <PricePanel
              label="DC"
              hook={dc}
              onEdit={() =>
                navigate("/default-prices/edit", {
                  state: {
                    stationPrices: dc.stationPrices,
                    originalResponse: dc.originalResponse,
                    stationType: "DC",
                  },
                })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const PricePanel: React.FC<{
  label: string;
  hook: ReturnType<typeof useStationPrice>;
  onEdit: () => void;
}> = ({ label, hook, onEdit }) => {
  const { stationPrices, lastUpdated, isLoading } = hook;

  return (
    <Card className="h-full">
      <div className="flex justify-content-between align-items-center mb-3">
        <div className="text-xl font-semibold text-900">{label} Station</div>
        {stationPrices && (
          <Button label="Edit" icon="pi pi-pencil" size="small" onClick={onEdit} />
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-column align-items-center gap-3 py-5">
          <ProgressSpinner style={{ width: "40px", height: "40px" }} />
        </div>
      ) : stationPrices ? (
        <>
          <Divider className="mt-0" />
          <div className="flex flex-column gap-1">
            <PriceRow
              icon="pi-dollar"
              iconColor="#f59e0b"
              label="Transaction Fee"
              value={stationPrices.transactionFee?.toFixed(2)}
              unit="EUR"
            />
            <PriceRow
              icon="pi-bolt"
              iconColor="#10b981"
              label="Energy Price"
              value={stationPrices.energyPrice?.toFixed(2)}
              unit="EUR/kWh"
            />
            <PriceRow
              icon="pi-clock"
              iconColor="#ef4444"
              label="Blocking Fee"
              value={stationPrices.blockingFee?.price?.toFixed(2)}
              unit="EUR/min"
              note={
                stationPrices.blockingFee?.minDuration
                  ? `Applies after ${stationPrices.blockingFee.minDuration} min`
                  : undefined
              }
            />
            <PriceRow
              icon="pi-stop-circle"
              iconColor="#3b82f6"
              label="Blocking Fee Cap"
              value={stationPrices.blockingFeeCap?.price?.toFixed(2)}
              unit="EUR"
            />
          </div>
          <div className="text-color-secondary text-xs mt-4">
            <i className="pi pi-calendar mr-1" />
            Last updated: {formatDate(lastUpdated)}
            &nbsp;·&nbsp;Prices include VAT (19%)
          </div>
        </>
      ) : (
        <div className="flex flex-column align-items-center py-5 gap-2 text-color-secondary">
          <i className="pi pi-exclamation-circle text-3xl" />
          <span className="text-sm">No pricing data available</span>
        </div>
      )}
    </Card>
  );
};

const PriceRow: React.FC<{
  icon: string;
  iconColor: string;
  label: string;
  value?: string;
  unit: string;
  note?: string;
}> = ({ icon, iconColor, label, value, unit, note }) => (
  <div className="flex align-items-start gap-3 p-3 border-round surface-50 mb-2">
    <div
      className="flex align-items-center justify-content-center border-circle"
      style={{ width: "36px", height: "36px", background: `${iconColor}18`, flexShrink: 0 }}
    >
      <i className={`pi ${icon}`} style={{ color: iconColor, fontSize: "1rem" }} />
    </div>
    <div className="flex flex-column flex-1">
      <span className="text-color-secondary text-sm">{label}</span>
      <span className="font-semibold text-900 text-lg">
        {value ?? "N/A"}{" "}
        <span className="text-color-secondary font-normal text-sm">{unit}</span>
      </span>
      {note && <span className="text-xs text-color-secondary mt-1">{note}</span>}
    </div>
  </div>
);

export default DefaultPrices;
